// Contracts — kids ADMIN cabinet (2026-06-28). Staff management (users: role /
// office / pid), office CMS-lite content, office hours + overrides. Bearer
// admin|manager; admin is scoped to its own office, manager is global. Shapes
// snapped from migrations/0002 (users, office_content, office_hours,
// office_hours_overrides). FINANCE endpoints are intentionally NOT here — the
// 0003 finance schema (office_rates / mod_ledger) is still DRAFT (see index.ts TODO).
import { z } from 'zod';
import { KidsRoleSchema } from './enums';
import { KidsClusterSchema } from './goal';

const TIME_RE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ── GET /api/kids/admin/users?office=&role=&q= ───────────────────────────────
export const KidsAdminUsersQuerySchema = z.object({
  office: z.string().trim().max(60).optional(), // manager only; admin forced to own
  role: KidsRoleSchema.optional(),
  // server-side substring search over name / email / pid / tg_login (SQL LIKE,
  // %/_ escaped worker-side). Combined (AND) with office/role + role scoping.
  q: z.string().trim().max(120).optional(),
  // lead-geo filter (0030): ISO-2 страна первого касания (signup_country).
  // Практический смысл — с role=parent («откуда лиды»); работает и без него.
  country: z.string().trim().toUpperCase().length(2).optional(),
});
export type KidsAdminUsersQuery = z.infer<typeof KidsAdminUsersQuerySchema>;

export const KidsAdminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  role: KidsRoleSchema,
  pid: z.string().nullable(),
  office: z.string().nullable(),
  country: z.string().nullable(),
  language: z.string().nullable(),
  roles: z.array(KidsRoleSchema),
  // per-moderator participant payout rate in CENTS (0016). null = office rate
  // applies. Optional so pre-0016 fixtures/clients keep validating.
  participantRate: z.number().int().nullable().optional(),
  // the staff member's OWN per-done-session rate in CENTS (0025 multilevel
  // finance). null = not assigned (no mod_earning accrues). Written via
  // POST /api/kids/finance/mod-rate — NOT via the admin user update.
  modSessionRate: z.number().int().nullable().optional(),
  // TG @username (stored WITHOUT the leading '@', same canon as me/profile).
  // Editable from the admin user card (Tim 04.07) — feeds the packer's channel
  // report tag and the bot auto-bind. Optional for pre-existing clients.
  tgLogin: z.string().nullable().optional(),
  // 0020: admin switch — show the balance card on the moderator's Home.
  // 0/1 from D1; optional so pre-0020 clients keep validating.
  showBalance: z.number().int().optional(),
  // 0035: per-moderator faceval gate (#44) — 0/1 from D1, MANAGER-set. 1 =
  // session form requires a photo + the duplicate check gates creation.
  facevalRequired: z.number().int().optional(),
  // Lead geo (0030): IP и страна визитёра НА МОМЕНТ создания лида в публичном
  // флоу (apply / remote-ленд / intake). NULL = стаффовое создание или лид до
  // 0030. НЕ путать с `country` выше — там страна офиса. Optional для
  // pre-0030 клиентов.
  signupIp: z.string().nullable().optional(),
  signupCountry: z.string().nullable().optional(),
});
export type KidsAdminUser = z.infer<typeof KidsAdminUserSchema>;

export const KidsAdminUsersResSchema = z.object({
  users: z.array(KidsAdminUserSchema),
});
export type KidsAdminUsersRes = z.infer<typeof KidsAdminUsersResSchema>;

// ── POST /api/kids/admin/user → edit name / role / office / pid ──────────────
// admin may only edit moderators in its OWN office; manager may edit anyone.
// Email stays immutable (it's the sign-in identity). name is free-form (staff
// hold Cyrillic display names like «Олег МСК») — Tim 02.07: «менять имя».
export const KidsAdminUserUpdateReqSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1).max(80).optional(),
  role: KidsRoleSchema.optional(),
  office: z.string().trim().max(60).nullable().optional(),
  pid: z
    .string()
    .trim()
    .regex(/^\d{2}$/, 'pid must be 2 digits')
    .nullable()
    .optional(),
  // per-moderator participant payout rate in CENTS (Tim 03.07). null clears it
  // (office rate applies again). Same bounds as the office-rate editor.
  participantRate: z.number().int().nonnegative().max(10_000_000).nullable().optional(),
  // TG @username — same shape canon as me/profile (auth.ts TG_LOGIN_RE); the
  // worker strips the leading '@' before storing. null clears the tag.
  tgLogin: z
    .string()
    .trim()
    .regex(/^@?[A-Za-z0-9_]{1,40}$/, 'telegram login: letters/digits/_ only')
    .max(41)
    .nullable()
    .optional(),
  // 0020: toggle the moderator's Home balance card (admin/manager control).
  showBalance: z.boolean().optional(),
  // 0035: per-moderator faceval gate (#44). MANAGER-only — the worker 403s an
  // office admin trying to flip it (Tim 16.07: включает менеджер со своего
  // аккаунта, точечно на конкретных модераторов).
  facevalRequired: z.boolean().optional(),
});
export type KidsAdminUserUpdateReq = z.infer<typeof KidsAdminUserUpdateReqSchema>;

export const KidsAdminUserUpdateResSchema = z.object({
  ok: z.literal(true),
  user: KidsAdminUserSchema,
  // pending done+unpaid sessions of THIS moderator auto-debited right after a
  // personal rate was set (mirrors the office-rate backfill counter).
  backfilled: z.number().optional(),
});
export type KidsAdminUserUpdateRes = z.infer<typeof KidsAdminUserUpdateResSchema>;

// ── POST /api/kids/admin/user-delete → manager removes an account ────────────
// Manager-only (Tim 02.07: «удалять (менеджер онли)»). Refuses to delete the
// caller itself and any user still referenced by data (sessions/meetings/
// children/finance/links) — those come back as `user_has_data` with per-table
// counts so the UI can explain what's holding the account.
export const KidsAdminUserDeleteReqSchema = z.object({
  id: z.string().min(1),
});
export type KidsAdminUserDeleteReq = z.infer<typeof KidsAdminUserDeleteReqSchema>;

export const KidsAdminUserDeleteResSchema = z.object({ ok: z.literal(true) });
export type KidsAdminUserDeleteRes = z.infer<typeof KidsAdminUserDeleteResSchema>;

// ── GET /api/kids/admin/participants?office=&q=&offset= ──────────────────────
// Participant (children) directory for the admin cabinet «Участники» view.
// Bearer admin|manager with the users-list scoping: manager sees every office
// (free office filter), admin is forced to its OWN office, everyone else 403s.
// `q` searches BOTH the child's name and the parent's name (LIKE, escaped).
// Paged (fixed server page = 50; 1200+ children in prod): `offset` walks pages,
// the Res mirrors the monitor's total/offset/limit envelope.
export const KidsAdminParticipantsQuerySchema = z.object({
  office: z.string().trim().max(60).optional(), // manager only; admin forced to own
  q: z.string().trim().max(120).optional(),
  offset: z.coerce.number().int().min(0).max(100000).optional(),
});
export type KidsAdminParticipantsQuery = z.infer<typeof KidsAdminParticipantsQuerySchema>;

export const KidsAdminParticipantSchema = z.object({
  id: z.string(),
  name: z.string(),
  age: z.number().int().nullable(),
  gender: z.string().nullable(),
  ethnicity: z.string().nullable(),
  // office of the child's meeting (children.meeting_id → meetings.office — the
  // session-office canon from scope.ts; parents' users.office is usually NULL).
  office: z.string().nullable(),
  // the parent account owning the child (children.user_id → users)
  parentName: z.string().nullable(),
  parentEmail: z.string().nullable(),
  parentPhone: z.string().nullable(),
  // COUNT of this child's sessions with status='done'
  doneSessions: z.number().int().nonnegative(),
});
export type KidsAdminParticipant = z.infer<typeof KidsAdminParticipantSchema>;

export const KidsAdminParticipantsResSchema = z.object({
  total: z.number().int().nonnegative(), // rows matching the filter (all pages)
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(), // server page size (50)
  participants: z.array(KidsAdminParticipantSchema),
});
export type KidsAdminParticipantsRes = z.infer<typeof KidsAdminParticipantsResSchema>;

// ── GET /api/kids/admin/offices ──────────────────────────────────────────────
// Plain office list for editor selects (role/office pickers). manager → every
// active office; admin → only its own. Canonical source is the D1 `offices`
// table (seeded, not hard-coded), so selects load this instead of an Atlanta-only
// static list. Shape snapped from offices (office PK, name NOT NULL, cluster_slug).
export const KidsAdminOfficeSchema = z.object({
  office: z.string(),
  name: z.string(),
  cluster: z.string(), // cluster_slug: 'atlanta' | 'remote'
});
export type KidsAdminOffice = z.infer<typeof KidsAdminOfficeSchema>;

export const KidsAdminOfficesResSchema = z.object({
  offices: z.array(KidsAdminOfficeSchema),
});
export type KidsAdminOfficesRes = z.infer<typeof KidsAdminOfficesResSchema>;

// ── POST /api/kids/admin/office → manager creates a new office ────────────────
// Manager-only (admin is scoped to its own office, can't spin up new ones). The
// worker derives the office slug from `name` (latin→lower, non-alnum→'_', e.g.
// "Kris LA" → "kris_la"); a blank slug → bad_name, a collision → office_exists.
// `city` is the raw shooting city (offices.city, migration 0008). `timezone` is
// a validated IANA-ish zone (Area/Location); `cluster` maps to cluster_slug.
const IANA_TZ_RE = /^[A-Za-z]+\/[A-Za-z_]+(?:\/[A-Za-z_]+)?$/;

export const KidsAdminOfficeCreateReqSchema = z.object({
  name: z.string().trim().min(1).max(120),
  city: z.string().trim().max(120).optional(),
  timezone: z.string().trim().min(1).regex(IANA_TZ_RE, 'timezone must be an IANA zone'),
  cluster: KidsClusterSchema,
});
export type KidsAdminOfficeCreateReq = z.infer<typeof KidsAdminOfficeCreateReqSchema>;

export const KidsAdminOfficeCreateResSchema = z.object({
  ok: z.literal(true),
  office: z.object({
    office: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    cluster: KidsClusterSchema,
    timezone: z.string(),
  }),
});
export type KidsAdminOfficeCreateRes = z.infer<typeof KidsAdminOfficeCreateResSchema>;

// ── office content (CMS-lite) ────────────────────────────────────────────────
// GET /api/kids/admin/office-content?office=
export const KidsAdminOfficeContentQuerySchema = z.object({
  office: z.string().trim().max(60).optional(), // manager only; admin forced to own
});
export type KidsAdminOfficeContentQuery = z.infer<typeof KidsAdminOfficeContentQuerySchema>;

export const KidsOfficeContentBlockSchema = z.object({
  blockKey: z.string(),
  content: z.string(),
  updatedAt: z.number().int().nullable(),
});
export type KidsOfficeContentBlock = z.infer<typeof KidsOfficeContentBlockSchema>;

export const KidsAdminOfficeContentResSchema = z.object({
  office: z.string(),
  blocks: z.array(KidsOfficeContentBlockSchema),
});
export type KidsAdminOfficeContentRes = z.infer<typeof KidsAdminOfficeContentResSchema>;

// PUT /api/kids/admin/office-content → upsert one or more blocks.
export const KidsAdminOfficeContentUpdateReqSchema = z.object({
  office: z.string().trim().min(1).max(60),
  blocks: z
    .array(
      z.object({
        blockKey: z.string().trim().min(1).max(80),
        content: z.string().max(20000),
      }),
    )
    .min(1)
    .max(40),
});
export type KidsAdminOfficeContentUpdateReq = z.infer<typeof KidsAdminOfficeContentUpdateReqSchema>;

// ── office hours + overrides ─────────────────────────────────────────────────
// GET /api/kids/admin/office-hours?office=
export const KidsAdminOfficeHoursQuerySchema = z.object({
  office: z.string().trim().max(60).optional(),
});
export type KidsAdminOfficeHoursQuery = z.infer<typeof KidsAdminOfficeHoursQuerySchema>;

export const KidsOfficeHourSchema = z.object({
  weekday: z.number().int().min(0).max(6), // 0=Sun..6=Sat
  startTime: z.string().regex(TIME_RE),
  endTime: z.string().regex(TIME_RE),
});
export type KidsOfficeHour = z.infer<typeof KidsOfficeHourSchema>;

export const KidsOfficeHourOverrideSchema = z.object({
  date: z.string().regex(DATE_RE),
  closed: z.boolean(),
  startTime: z.string().regex(TIME_RE).nullable(),
  endTime: z.string().regex(TIME_RE).nullable(),
  reason: z.string().nullable(),
});
export type KidsOfficeHourOverride = z.infer<typeof KidsOfficeHourOverrideSchema>;

export const KidsAdminOfficeHoursResSchema = z.object({
  office: z.string(),
  hours: z.array(KidsOfficeHourSchema),
  overrides: z.array(KidsOfficeHourOverrideSchema),
});
export type KidsAdminOfficeHoursRes = z.infer<typeof KidsAdminOfficeHoursResSchema>;

// PUT /api/kids/admin/office-hours → replace the weekly grid + overrides for an
// office (full-set write: the supplied arrays become the new truth).
export const KidsAdminOfficeHoursUpdateReqSchema = z.object({
  office: z.string().trim().min(1).max(60),
  hours: z.array(KidsOfficeHourSchema).max(40),
  overrides: z.array(KidsOfficeHourOverrideSchema).max(120),
});
export type KidsAdminOfficeHoursUpdateReq = z.infer<typeof KidsAdminOfficeHoursUpdateReqSchema>;

export const KidsAdminAckResSchema = z.object({ ok: z.literal(true) });
export type KidsAdminAckRes = z.infer<typeof KidsAdminAckResSchema>;
